import React, { useState } from 'react';
import { getContactsForDocument, postContactsCreate } from '../../../helpers/backend';
import { useSelector } from 'react-redux';

import { TbChevronRight } from "react-icons/tb";

const PatientStep = ({ patientInfo, onInputChange, onBack, onNextStep }) => {
  const [loading, setLoading] = useState(false);
  const [patientData, setPatientData] = useState(null);

  const searchPatientByDocument = async (documentNumber) => {
    if (documentNumber && documentNumber.length >= 6) {
      setLoading(true);
      try {
        const response = await getContactsForDocument(`?search=${documentNumber}`);
        if (response.data?.results?.length > 0) {
          const patient = response.data.results[0];
          setPatientData(patient);

          // Actualizar campos con datos del paciente existente
          onInputChange({ target: { name: 'name', value: patient.get_full_name || `${patient.first_name} ${patient.last_name}` || '' } });
          onInputChange({ target: { name: 'email', value: patient.email?.[0]?.email || patient.username || '' } });
          onInputChange({ target: { name: 'phone', value: patient.phone?.[0]?.telefono || '' } });

          // ACTUALIZAR contactId externo
          onInputChange({ target: { name: 'id', value: patient.id } });

        } else {
          setPatientData(null);
          // Si no se encuentra, aseguramos que no haya ID previo
          onInputChange({ target: { name: 'id', value: '' } });
        }
      } catch (error) {
        console.error("Error fetching patient data:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDocumentBlur = (e) => {
    searchPatientByDocument(e.target.value);
  };

  const handleSaveAndContinue = async () => {
    const { name, phone, email, documentNumber } = patientInfo;

    if (!name || !phone || !email || !documentNumber) {
      alert("Completa todos los campos obligatorios antes de continuar.");
      return;
    }

    if (patientData) {
      // Paciente ya existe, simplemente continuar
      onNextStep();
      return;
    }

    const formData = {
      first_name: name,
      phone,
      username: email,
      documentNumber: documentNumber,
      documentType: "CEDULA_CIUDADANIA"
    };

    try {
      const response = await postContactsCreate(formData);
      const newPatientId = response.data?.id;
      if (newPatientId) {
        alert("Paciente creado exitosamente.");

        // Actualizar contactId externo con el nuevo ID
        onInputChange({ target: { name: 'id', value: newPatientId } });

        onNextStep();
      } else {
        throw new Error("No se recibió ID del nuevo paciente.");
      }
    } catch (error) {
      console.error("Error al crear el paciente:", error);
      alert("Error al crear el paciente. Intenta nuevamente.");
    }
  };

  const theme = useSelector((state) => state.theme.theme) || "light";

  return (
    <div className="step-content_">
      <h2 className="step-title">Datos del Paciente</h2>
      <p className="step-subtitle">Completa tus datos para confirmar la cita</p>

      <div className="form-container">
        <div className="form-section">
          <div className="form-group">
            <label style={{ color: theme === "dark" && "#c3cbe4" }}>
              Número de identificación <span className="required">*</span>
            </label>
            <input
              type="text"
              name="documentNumber"
              value={patientInfo.documentNumber || ""}
              onChange={onInputChange}
              onBlur={handleDocumentBlur}
              placeholder="Ingresa tu número de documento completo"
              className="input_background"
            />
            {loading && (
              <div className="loading-spinner-cedula" style={{fontSize:".9rem"}}>
                <i className="fas fa-spinner fa-spin"></i> Buscando paciente...
              </div>
            )}
            {!loading && patientData && (
              <div className="patient-found-badge" style={{fontSize:".9rem"}}>
                <i className="fas fa-check-circle"></i> Paciente encontrado:{" "}
                {patientData.get_full_name}
              </div>
            )}
            {!loading &&
              !patientData &&
              patientInfo.documentNumber?.length >= 6 && (
                <div className="patient-not-found-msg" style={{fontSize:".9rem"}}>
                  <i className="fas fa-exclamation-circle"></i> No se encontró
                  el paciente. Puedes completar los datos y continuar.
                </div>
              )}
          </div>

          <div className="form-group">
            <label style={{ color: theme === "dark" && "#c3cbe4" }}>
              Nombre completo <span className="required">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={patientInfo.name || ""}
              onChange={onInputChange}
              placeholder="Ingresa tu nombre completo"
              className="input_background"
            />
          </div>

          <div className="form-group">
            <label style={{ color: theme === "dark" && "#c3cbe4" }}>
              Teléfono <span className="required">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={patientInfo.phone || ""}
              onChange={onInputChange}
              placeholder="Ingresa tu número de contacto"
              className="input_background"
            />
          </div>
        </div>

        <div className="form-section">
          <div className="form-group">
            <label style={{ color: theme === "dark" && "#c3cbe4" }}>
              Correo electrónico <span className="required">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={patientInfo.email || ""}
              onChange={onInputChange}
              placeholder="ejemplo@correo.com"
              className="input_background"
            />
          </div>

          <div className="form-group">
            <label style={{ color: theme === "dark" && "#c3cbe4" }}>
              Entidad de salud
            </label>
            <input
              type="text"
              name="insurance"
              value={patientInfo.insurance || ""}
              onChange={onInputChange}
              placeholder="EPS, prepagada, particular"
              className="input_background"
            />
          </div>

          <div className="form-group">
            <label style={{ color: theme === "dark" && "#c3cbe4" }}>
              Motivo de la consulta
            </label>
            <textarea
              name="reason"
              value={patientInfo.reason || ""}
              onChange={onInputChange}
              placeholder="Describe brevemente el motivo de tu consulta"
              rows="3"
              className="input_background"
            />
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button className="back-btn" onClick={onBack}>
          Volver a citas
        </button>
        <button
          className="next-btn"
          onClick={handleSaveAndContinue}
          disabled={
            !patientInfo.name ||
            !patientInfo.phone ||
            !patientInfo.email ||
            !patientInfo.documentNumber
          }
        >
          <span>Guardar y continuar</span>
          <span style={{ marginRight: "-.5rem" }}>
            <TbChevronRight size={21} />
          </span>
        </button>
      </div>
    </div>
  );
};

export default PatientStep;
